/**
 * A finger-signing surface, ported from `SignaturePadView.kt`.
 *
 * Strokes are kept as point lists rather than painted straight into the canvas,
 * so the signature can be exported larger than the screen and so undo can drop
 * the last stroke. Pointer events rather than touch events: one code path covers
 * finger, stylus and mouse, and `setPointerCapture` keeps a stroke alive if the
 * finger leaves the canvas.
 */

interface Point {
  x: number
  y: number
}

export interface SignatureExport {
  blob: Blob
  bytes: Uint8Array
  dataUrl: string
  width: number
  height: number
}

export class SignaturePad {
  readonly canvas: HTMLCanvasElement
  private strokes: Point[][] = []
  private active: Point[] | null = null
  private ratio = 1
  private onChange: (hasSignature: boolean) => void

  constructor(canvas: HTMLCanvasElement, onChange: (hasSignature: boolean) => void = () => {}) {
    this.canvas = canvas
    this.onChange = onChange

    canvas.addEventListener('pointerdown', this.down)
    canvas.addEventListener('pointermove', this.move)
    canvas.addEventListener('pointerup', this.up)
    canvas.addEventListener('pointercancel', this.up)
    // Without this a drag on the canvas scrolls the page instead of drawing.
    canvas.style.touchAction = 'none'
  }

  get hasSignature(): boolean {
    return this.strokes.length > 0
  }

  /** Sizes the backing store to the element, accounting for device pixel ratio. */
  resize(): void {
    const rect = this.canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    this.ratio = window.devicePixelRatio || 1
    this.canvas.width = Math.round(rect.width * this.ratio)
    this.canvas.height = Math.round(rect.height * this.ratio)
    this.redraw()
  }

  clear(): void {
    this.strokes = []
    this.active = null
    this.redraw()
    this.onChange(false)
  }

  undo(): void {
    this.strokes.pop()
    this.redraw()
    this.onChange(this.hasSignature)
  }

  destroy(): void {
    this.canvas.removeEventListener('pointerdown', this.down)
    this.canvas.removeEventListener('pointermove', this.move)
    this.canvas.removeEventListener('pointerup', this.up)
    this.canvas.removeEventListener('pointercancel', this.up)
  }

  private pointFrom(event: PointerEvent): Point {
    const rect = this.canvas.getBoundingClientRect()
    return { x: (event.clientX - rect.left) * this.ratio, y: (event.clientY - rect.top) * this.ratio }
  }

  private down = (event: PointerEvent): void => {
    event.preventDefault()
    this.canvas.setPointerCapture(event.pointerId)
    const wasEmpty = !this.hasSignature
    const point = this.pointFrom(event)
    // A tap should still leave a visible dot.
    this.active = [point, { x: point.x + 0.1, y: point.y }]
    this.strokes.push(this.active)
    this.redraw()
    if (wasEmpty) this.onChange(true)
  }

  private move = (event: PointerEvent): void => {
    if (!this.active) return
    event.preventDefault()
    this.active.push(this.pointFrom(event))
    this.redraw()
  }

  private up = (event: PointerEvent): void => {
    if (!this.active) return
    event.preventDefault()
    this.active = null
    this.onChange(this.hasSignature)
  }

  private strokeWidth(): number {
    return 2.5 * this.ratio
  }

  private redraw(): void {
    const ctx = this.canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    // Guide line to sign above, at the same proportion as the Android pad.
    ctx.strokeStyle = '#b0b0b0'
    ctx.lineWidth = 1 * this.ratio
    ctx.beginPath()
    const baseline = this.canvas.height * 0.78
    ctx.moveTo(this.canvas.width * 0.06, baseline)
    ctx.lineTo(this.canvas.width * 0.94, baseline)
    ctx.stroke()

    this.paintStrokes(ctx, this.strokeWidth(), 0, 0, 1)
  }

  private paintStrokes(
    ctx: CanvasRenderingContext2D,
    width: number,
    offsetX: number,
    offsetY: number,
    scale: number,
  ): void {
    ctx.strokeStyle = '#000000'
    ctx.lineWidth = width
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    for (const stroke of this.strokes) {
      if (stroke.length < 2) continue
      ctx.beginPath()
      const first = stroke[0]!
      ctx.moveTo((first.x - offsetX) * scale, (first.y - offsetY) * scale)
      // Quadratic smoothing through the midpoints, so a fast finger does not
      // come out as a polygon.
      for (let i = 1; i < stroke.length - 1; i++) {
        const a = stroke[i]!
        const b = stroke[i + 1]!
        ctx.quadraticCurveTo(
          (a.x - offsetX) * scale,
          (a.y - offsetY) * scale,
          ((a.x + b.x) / 2 - offsetX) * scale,
          ((a.y + b.y) / 2 - offsetY) * scale,
        )
      }
      const last = stroke[stroke.length - 1]!
      ctx.lineTo((last.x - offsetX) * scale, (last.y - offsetY) * scale)
      ctx.stroke()
    }
  }

  /**
   * Exports a transparent PNG cropped to the ink, rendered larger than the
   * screen so it stays sharp in the PDF.
   */
  async export(scale = 2): Promise<SignatureExport | null> {
    if (!this.hasSignature) return null

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const stroke of this.strokes) {
      for (const point of stroke) {
        minX = Math.min(minX, point.x)
        minY = Math.min(minY, point.y)
        maxX = Math.max(maxX, point.x)
        maxY = Math.max(maxY, point.y)
      }
    }
    const pad = this.strokeWidth() * 2
    minX = Math.max(0, minX - pad)
    minY = Math.max(0, minY - pad)
    maxX = Math.min(this.canvas.width, maxX + pad)
    maxY = Math.min(this.canvas.height, maxY + pad)
    const width = Math.max(1, Math.round((maxX - minX) * scale))
    const height = Math.max(1, Math.round((maxY - minY) * scale))

    const out = document.createElement('canvas')
    out.width = width
    out.height = height
    const ctx = out.getContext('2d')
    if (!ctx) return null
    this.paintStrokes(ctx, this.strokeWidth() * scale, minX, minY, scale)

    const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'))
    if (!blob) return null
    const bytes = new Uint8Array(await blob.arrayBuffer())
    return { blob, bytes, dataUrl: out.toDataURL('image/png'), width, height }
  }
}
